// Santé des services (SA-9.1) — porté de la maquette Super Admin v2 (bloc `isHealth`) : une carte par
// service surveillé (pastille, latence sur jauge graduée aux PALIERS officiels 100/300/500 ms, uptime,
// dernier code HTTP), carte cliquée → panneau de détail. ⚠️ §23.0 : tout vient de la SONDE réelle
// (`monitored_service` + prober : latence mesurée, médiane/p95 sur les relevés, uptime, code) ; la maquette
// montrait aussi les BRIQUES INTERNES de chaque service (PostgreSQL, files, SMTP…) — ces dépendances ne
// sont pas sondées aujourd'hui → panneau à un seul niveau, qui le DIT (jamais des briques inventées).
import { useEffect, useMemo, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import { api, type HealthMeasure, type History, type ServiceHealth } from '../api';
import { DiskPanel } from './health-disk';
import { fmtUptime, HEALTH_STATUS } from '../service-health';
import { HistoryChart, RANGES, trousDeMesure } from '../history-chart';
import { Chip } from '../ui';
import { useAuth, isSuperadmin } from '../auth';
import { EmptyState, Icon } from '@orgazone/ui-web';

/**
 * CE QUI S'EST PASSÉ AU DERNIER RELEVÉ, en clair.
 *
 * ⚠️ « AUCUNE RÉPONSE » NE SUFFISAIT PAS quand c'est la PASSERELLE qui a renoncé : arrêté
 * volontairement en bêta, le store privé faisait répondre 502 au balanceur au bout de 3 s, et l'écran
 * n'avait aucun moyen de dire lequel des deux maillons manquait. Nommer le code oriente le dépannage :
 * 502 dit « le service derrière ne répond pas », un silence total dit « le balanceur lui-même est
 * hors d'atteinte ».
 */
function motifDe(s: ServiceHealth): string {
  if (s.status === 'down') {
    return s.lastCode !== null ? `passerelle HTTP ${s.lastCode} — le service ne répond pas` : 'aucune réponse';
  }
  return s.lastCode !== null ? `HTTP ${s.lastCode}` : 'en attente du 1ᵉʳ relevé';
}

// Position sur la jauge 0→700+ ms (échelle de la maquette, paliers aux mêmes abscisses).
const gaugePct = (ms: number | null): number => (ms === null ? 0 : Math.min(100, (ms / 700) * 100));

const scaleLbl: CSSProperties = { position: 'absolute', fontSize: 9, color: 'var(--ink400)' };

/**
 * Bloc d'HISTORIQUE du service sélectionné : bande min–max + médiane, périodes bornées par la rétention.
 * ⚠️ Il ne prétend jamais couvrir plus que ce qui est conservé — la rétention est écrite à l'écran,
 * sinon un graphe vide se lirait « il ne s'est rien passé » au lieu de « on ne le garde pas » (§23.0).
 */
function HistoryPanel({ service, measure }: { service: ServiceHealth; measure: HealthMeasure }) {
  const [hours, setHours] = useState(24);
  const [data, setData] = useState<History | null>(null);
  const [err, setErr] = useState<string | null>(null);
  // Garde anti-réponse périmée : changer de service ou de période relance un appel, et rien ne
  // garantit l'ordre de retour — sans jeton, un graphe ancien peut écraser le nouveau.
  const jeton = useRef(0);

  useEffect(() => {
    const n = ++jeton.current;
    setData(null);
    api.healthHistory(service.id, hours)
      .then((r) => { if (n === jeton.current) { setData(r); setErr(null); } })
      .catch((e) => { if (n === jeton.current) { setData(null); setErr((e as Error).message); } });
  }, [service.id, hours]);

  const pannes = data?.buckets.reduce((n, b) => n + b.failures, 0) ?? 0;
  // ⚠️ Comptés par la MÊME fonction que le graphe : deux règles de seuil finiraient par diverger, et
  // le pied annoncerait un nombre de trous que le tracé ne montre pas.
  const trous = data ? trousDeMesure(data.buckets, data.bucketSeconds * 1000).length : 0;

  return (
    <div className="dcard" style={{ overflow: 'hidden', marginBottom: 14 }}>
      <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10, flexWrap: 'wrap' }}>
        <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>{service.name} — historique de latence</span>
        <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink400)' }}>bande min–max · ligne médiane</span>
        <div style={{ flex: 1 }} />
        <div style={{ display: 'flex', gap: 4 }}>
          {RANGES.map((r) => (
            <Chip key={r.k} tone="info" on={hours === r.hours} onClick={() => setHours(r.hours)}>{r.label}</Chip>
          ))}
        </div>
      </div>

      <div style={{ padding: '10px 12px 4px' }}>
        {err ? <div className="err" style={{ margin: 8 }}>Historique indisponible : {err}</div>
          : !data ? <div className="muted" style={{ padding: 24, textAlign: 'center' }}>Lecture de l’historique…</div>
            : <HistoryChart data={data} measure={measure} />}
      </div>

      <div style={{ padding: '8px 18px 12px', fontSize: 'var(--t-meta)', color: 'var(--ink500)', display: 'flex', gap: 14, flexWrap: 'wrap' }}>
        <span>Historique conservé <b>{data?.retentionDays ?? 7} jours</b>.</span>
        {data && <span>Un point = <b>{data.bucketSeconds >= 60 ? `${Math.round(data.bucketSeconds / 60)} min` : `${data.bucketSeconds} s`}</b> de relevés.</span>}
        {/* Une bande rouge n'a de sens que si on dit ce qu'elle est. */}
        {pannes > 0 && <span style={{ color: 'var(--da-fg)' }}>■ {pannes} relevé{pannes > 1 ? 's' : ''} sans réponse sur la période.</span>}
        {/* ⚠️ Et un TROU n'est pas une panne : gris, pas rouge. Le distinguer par écrit autant que par
            le dessin — sans quoi une sonde arrêtée se raconterait comme un incident de service. */}
        {trous > 0 && <span>▨ {trous} trou{trous > 1 ? 's' : ''} de mesure (sonde arrêtée) — jamais relié{trous > 1 ? 's' : ''}.</span>}
      </div>
    </div>
  );
}

/**
 * LES BRIQUES D'UN SERVICE — ce qui le porte, mesuré brique par brique.
 *
 * ⚠️ CETTE LISTE VIENT DU SERVICE, ELLE N'EST PAS TENUE ICI. Chaque service déclare ses dépendances
 * dans son `registerHealth` ; la sonde les relit à chaque appel de `/v1/ready`. Une liste maintenue
 * côté supervision serait une seconde vérité — fausse dès qu'un service en ajoute une.
 *
 * ⚠️ ELLE NE COÛTE AUCUNE REQUÊTE. Le corps de `/v1/ready` traversait déjà le réseau toutes les
 * 30 secondes ; la sonde le jetait (`res.resume()`). On le lit, c'est tout.
 */
function BriquesSection({ service }: { service: ServiceHealth }) {
  const deps = service.deps;
  return (
    <>
      {/* ⚠️ UNE SECTION, PAS UNE CARTE. Ces briques décrivent LE MÊME service que les chiffres
          au-dessus : les séparer en deux cartes obligeait l'œil à faire l'aller-retour entre deux
          surfaces pour lire un seul sujet — et intercalait une carte entre la courbe et ses chiffres.
          Une carte = un sujet ; ici le sujet est « ce que la sonde sait de ce service ». */}
      <div style={{ padding: '13px 18px', borderTop: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10 }}>
        <span className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>
          Briques internes
        </span>
        <div style={{ flex: 1 }} />
        {deps && deps.length > 0 && (
          <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>
            {deps.length} composant{deps.length > 1 ? 's' : ''}
          </span>
        )}
      </div>

      {/* ⚠️ TROIS ÉTATS DISTINCTS, et les confondre ferait lire une lacune comme un fait :
          `undefined` = pas encore mesuré · vide = ce service n'a vraiment aucune dépendance ·
          rempli = la liste. Un « aucune dépendance » affiché avant le premier relevé serait faux. */}
      {!deps ? (
        <div className="muted" style={{ padding: '16px 18px', fontSize: 'var(--t-body)' }}>
          {service.status === 'pending'
            ? 'Premier relevé en cours…'
            : 'Ce service ne déclare pas encore le détail de ses briques.'}
        </div>
      ) : deps.length === 0 ? (
        <div className="muted" style={{ padding: '16px 18px', fontSize: 'var(--t-body)' }}>
          Aucune dépendance — ce service est sans état&nbsp;: ni base, ni volume. Il n’a rien à déclarer prêt.
        </div>
      ) : (
        deps.map((d, i) => {
          const sain = d.status === 'ok';
          return (
            <div
              key={d.name}
              style={{
                display: 'grid', gridTemplateColumns: '10px minmax(0,1fr) minmax(90px,220px) 64px',
                alignItems: 'center', gap: 14, padding: '12px 18px',
                borderTop: i === 0 ? 'none' : '1px solid var(--ink200)',   // le filet de section fait office de premier séparateur
              }}
            >
              <span className="dot" style={{ background: sain ? 'var(--chart-ok)' : 'var(--chart-da)' }} />

              <div style={{ minWidth: 0 }}>
                <div className="mono" style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{d.name}</div>
                {d.kind && (
                  <div style={{ fontSize: 'var(--t-note)', color: 'var(--ink500)', marginTop: 2 }}>{d.kind}</div>
                )}
                {/* ⚠️ LE MOTIF RÉEL, jamais « indisponible » : « connection refused » et « password
                    authentication failed » n'appellent pas le même geste, et à 3 h du matin c'est
                    toute la différence. */}
                {!sain && (
                  <div className="mono" style={{ fontSize: 'var(--t-note)', color: 'var(--da-fg)', marginTop: 4, overflowWrap: 'anywhere' }}>
                    {d.status}
                  </div>
                )}
              </div>

              {/* La jauge partage l'échelle des tuiles (0 → 700 ms) : deux barres de la même page qui
                  ne diraient pas la même chose au même endroit se compareraient à tort. */}
              <div style={{ position: 'relative', height: 7, borderRadius: 'var(--r-r4)', background: 'var(--ink100)', overflow: 'hidden' }}>
                <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${gaugePct(d.ms)}%`, background: sain ? 'var(--chart-ok)' : 'var(--chart-da)', borderRadius: 'var(--r-r4)' }} />
              </div>

              <div style={{ textAlign: 'right' }}>
                <span className="mono" style={{ fontSize: 'var(--t-control)', fontWeight: 700 }}>{d.ms}</span>
                <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>&nbsp;ms</span>
              </div>
            </div>
          );
        })
      )}

    </>
  );
}

export function Health() {
  const { user } = useAuth();
  const [rows, setRows] = useState<ServiceHealth[] | null>(null);
  const [measure, setMeasure] = useState<HealthMeasure | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [sel, setSel] = useState<string | null>(null);

  useEffect(() => {
    let alive = true;
    const load = () => api.healthServices()
      .then((r) => { if (alive) { setRows(r.services); setMeasure(r.measure); setErr(null); } })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    load();
    const t = setInterval(load, 30_000);
    return () => { alive = false; clearInterval(t); };
  }, []);

  const selected = useMemo(() => rows?.find((s) => s.id === sel) ?? rows?.[0] ?? null, [rows, sel]);

  return (
    <div className="page">
      <div style={{ display: 'flex', alignItems: 'flex-end', flexWrap: 'wrap', gap: '12px 16px', marginBottom: 16 }}>
        <div style={{ minWidth: 0, flex: '1 1 320px' }}>
          <h1 className="page-title">Santé des services</h1>
          <div style={{ fontSize: 'var(--t-body)', color: 'var(--ink500)', marginTop: 3 }}>
            Les services de la plateforme, mesurés par la sonde.
            {/* ⚠️ Les paliers sont ceux du SERVEUR (la mesure) : tant qu'ils ne sont pas lus, on ne les écrit pas. */}
            {measure && <> Paliers sur la médiane : vert &lt; {measure.warnFromMs} ms · orange {measure.warnFromMs}–{measure.highFromMs} · rouge dès {measure.highFromMs}.</>}
          </div>
        </div>
        {/* Comment les tuiles sont mesurées, puis le chemin vers les sondes : une rangée, deux éléments de 36 px. */}
        <div style={{ display: 'flex', alignItems: 'center', flexWrap: 'wrap', gap: 8, flexShrink: 0 }}>
          {measure && (
            <span className="health-measure"
              title={`Médiane : la valeur du milieu des ${measure.samples} derniers relevés (${measure.minutes} minutes de mesure). Une lenteur passagère ne change pas la couleur ; elle reste visible dans la bande min–max du graphique d’historique.`}>
              <span className="health-measure-dot" aria-hidden="true" />
              <span className="mono health-measure-stat">Médiane</span>
              <span className="health-measure-rule" aria-hidden="true" />
              <span className="mono health-measure-window">{measure.samples} relevés · {measure.minutes} min</span>
            </span>
          )}
          {/* ⚠️ LA RECETTE `.btn-ghost` SEULE : un padding ou une taille de police écrits en ligne l'emportaient sur elle,
              rendaient ce bouton plus petit que ses voisins d'en-tête et cassaient son survol. */}
          {isSuperadmin(user) && (
            <Link to="/applications" className="btn-ghost health-probes">
              <Icon name="sliders" size={16} />
              <span>Gérer les sondes</span>
              <Icon name="arrowR" size={13} style={{ opacity: 0.55 }} />
            </Link>
          )}
        </div>
      </div>

      {err && <div className="err" style={{ marginBottom: 12 }}>Sonde indisponible : {err}</div>}
      {!rows && !err && <div className="muted" style={{ padding: 16 }}>Premiers relevés…</div>}
      {rows && rows.length === 0 && (
        <div className="dcard"><EmptyState icon="pulse" title="Aucun service surveillé" note="Ajoutez leurs sondes dans Applications (super-admin) — la Console mesurera leur latence et leurs relevés réussis à chaque relevé." /></div>
      )}

      {rows && rows.length > 0 && <>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(225px,1fr))', gap: 12, marginBottom: 16 }}>
          {rows.map((s) => {
            const tone = HEALTH_STATUS[s.status];
            const on = selected?.id === s.id;
            return (
              <button key={s.id} onClick={() => setSel(s.id)}
                className={on ? 'svc-pick selected' : 'svc-pick'}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                  <span className="dot" style={{ background: tone.dot }} />
                  <span style={{ fontSize: 'var(--t-lead)', fontWeight: 700 }}>{s.name}</span>
                  <div style={{ flex: 1 }} />
                  <span className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)' }}>{fmtUptime(s.uptime, s.windowMinutes)}</span>
                </div>
                <div style={{ display: 'flex', alignItems: 'baseline', gap: 6, marginTop: 10 }}>
                  <span className="mono" style={{ fontSize: 'var(--t-feature)', fontWeight: 800, color: tone.fg }}>{s.lastMs ?? '—'}</span>
                  <span className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)' }}>ms</span>
                </div>
                {/* Jauge graduée aux paliers officiels (0 · 100 · 300 · 500 · 700+). */}
                <div style={{ position: 'relative', height: 7, borderRadius: 'var(--r-r4)', background: 'var(--ink100)', marginTop: 11, overflow: 'hidden' }}>
                  <div style={{ position: 'absolute', inset: '0 auto 0 0', width: `${gaugePct(s.lastMs)}%`, background: tone.dot, borderRadius: 'var(--r-r4)' }} />
                </div>
                <div style={{ position: 'relative', height: 12, marginTop: 4 }}>
                  <span className="mono" style={{ ...scaleLbl, left: 0 }}>0</span>
                  <span className="mono" style={{ ...scaleLbl, left: '14.3%', transform: 'translateX(-50%)' }}>100</span>
                  <span className="mono" style={{ ...scaleLbl, left: '42.9%', transform: 'translateX(-50%)' }}>300</span>
                  <span className="mono" style={{ ...scaleLbl, left: '71.4%', transform: 'translateX(-50%)' }}>500</span>
                  <span className="mono" style={{ ...scaleLbl, right: 0 }}>700+</span>
                </div>
                <div className="mono" style={{ fontSize: 'var(--t-micro)', color: 'var(--ink500)', marginTop: 8 }}>
                  {motifDe(s)} · {tone.label}
                </div>
              </button>
            );
          })}
        </div>

        {/* HISTORIQUE — entre les tuiles (choisir) et le détail (lire ses chiffres) : il suit la MÊME
            sélection que le bloc de détail, jamais une seconde qui se désynchroniserait de lui. */}
        {selected && measure && <HistoryPanel service={selected} measure={measure} />}

        {selected && (
          <div className="dcard" style={{ overflow: 'hidden' }}>
            <div style={{ padding: '13px 18px', borderBottom: '1px solid var(--ink200)', display: 'flex', alignItems: 'center', gap: 10 }}>
              <span style={{ fontSize: 'var(--t-title)', fontWeight: 700 }}>{selected.name} — détail de la sonde</span>
              <div style={{ flex: 1 }} />
              <span className="mono" style={{ fontSize: 'var(--t-sub)', color: 'var(--ink500)' }}>{selected.samples} relevé{selected.samples > 1 ? 's' : ''}</span>
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit,minmax(150px,1fr))', gap: '14px 20px', padding: '16px 18px' }}>
              {([
                ['dernière latence', selected.lastMs === null ? '—' : `${selected.lastMs} ms`],
                ['médiane (décide la couleur)', selected.median === null ? '—' : `${selected.median} ms`],
                ['p95 (repère seul)', selected.p95 === null ? '—' : `${selected.p95} ms`],
                // « disponibilité » promettait plus que ce qu'on mesure : c'est la part des relevés OK
                // sur la fenêtre, pas un taux de service sur la durée.
                ['relevés OK', fmtUptime(selected.uptime, selected.windowMinutes)],
                ['dernier code HTTP', selected.lastCode === null ? '—' : String(selected.lastCode)],
                ['sonde', selected.enabled ? 'active' : 'désactivée'],
              ] as const).map(([k, v]) => (
                <div key={k}>
                  <div className="mono" style={{ fontSize: 'var(--t-eyebrow)', letterSpacing: '.12em', textTransform: 'uppercase', color: 'var(--ink500)' }}>{k}</div>
                  <div className="mono" style={{ fontSize: 'var(--t-control)', fontWeight: 700, marginTop: 4 }}>{v}</div>
                </div>
              ))}
            </div>
            <div style={{ padding: '0 18px 14px' }}>
              <div className="mono" style={{ fontSize: 'var(--t-meta)', color: 'var(--ink500)', overflowWrap: 'anywhere' }}>{selected.url}</div>
            </div>

            {/* ⚠️ LES CHIFFRES D'ABORD, LES BRIQUES ENSUITE, DANS LA MÊME CARTE. Les chiffres du haut
                mesurent le service DE BOUT EN BOUT (chemin externe complet : balanceur, TLS, vhost) ;
                la liste du bas dit ce qu'il y a DEDANS. C'est la même question posée à deux échelles —
                les séparer en deux cartes faisait chercher la seconde moitié de la réponse ailleurs. */}
            <BriquesSection service={selected} />

            <div style={{ padding: '11px 18px', borderTop: '1px solid var(--ink200)', background: 'var(--ink100)', fontSize: 'var(--t-note)', color: 'var(--ink500)', lineHeight: 1.5 }}>
              {/* ⚠️ CE QU'ON NE MESURE PAS SE DIT (§23.0) : sans historique par brique, pas de « 99,97 % ».
                  Une colonne de disponibilité remplie au jugé serait le seul chiffre faux de cet écran. */}
              Les chiffres du haut mesurent le <strong>chemin externe complet</strong> (balanceur, TLS, vhost)&nbsp;;
              chaque brique, elle, est chronométrée à part au dernier relevé. La <strong>disponibilité par
              brique</strong> n’est pas encore tenue&nbsp;: elle demande un historique des changements d’état,
              qui n’existe pas à ce jour.
            </div>
          </div>
        )}
      </>}

      <DiskPanel />
    </div>
  );
}
